import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, Loader2, Sparkles, Copy, Trash2, RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { diagnosticsApi } from '../lib/api';

export default function AdminDiagnostics() {
  const { user, loading: userLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!userLoading && !user) navigate('/');
  }, [user, userLoading, navigate]);

  const load = useCallback(async (d) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await diagnosticsApi.summary(d);
      setData(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_founder) load(days);
  }, [user, days, load]);

  const copyPrompt = async () => {
    if (!data?.ai_fix_prompt) return;
    await navigator.clipboard.writeText(data.ai_fix_prompt);
    toast.success('Prompt copié — colle-le dans le chat Emergent ✨');
  };

  const clearAll = async () => {
    if (!window.confirm('Supprimer TOUS les rapports d\'erreur ?')) return;
    try {
      await diagnosticsApi.clear();
      toast.success('Rapports supprimés');
      load(days);
    } catch {
      toast.error('Suppression impossible');
    }
  };

  if (userLoading) {
    return <div className="pt-32 text-center"><Loader2 className="animate-spin text-pink-400 inline" /></div>;
  }
  if (!user) return null;
  if (!user.is_founder) {
    return (
      <div className="pt-32 max-w-md mx-auto text-center px-4">
        <ShieldCheck size={48} className="mx-auto text-zinc-500 mb-3" />
        <h2 className="text-2xl font-black mb-2">Accès réservé</h2>
        <p className="text-zinc-400">Cette page n&apos;est accessible qu&apos;au fondateur.</p>
      </div>
    );
  }

  return (
    <div className="relative pt-24 pb-16 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="admin-diagnostics">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-red-500 via-pink-500 to-purple-600 flex items-center justify-center">
              <Bug size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Auto-Diagnostics</h1>
              <p className="text-sm text-zinc-400">Erreurs runtime collectées + résumé IA prêt à coller dans Emergent.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-white/5 border border-white/10 text-white rounded-xl h-10 px-3 text-sm"
              data-testid="diag-days-select"
            >
              <option value={1}>Dernier jour</option>
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={30}>30 jours</option>
            </select>
            <Button onClick={() => load(days)} variant="outline" className="h-10 bg-white/5 border-white/15 text-white rounded-xl" data-testid="diag-refresh-btn">
              <RefreshCw size={14} className="mr-1.5" /> Actualiser
            </Button>
            <Button onClick={clearAll} variant="outline" className="h-10 bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20 rounded-xl" data-testid="diag-clear-btn">
              <Trash2 size={14} className="mr-1.5" /> Vider
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-pink-400" size={32} /></div>
        )}

        {!loading && err && (
          <div className="glass rounded-3xl p-6 border border-red-500/30 text-red-300 flex items-start gap-3" data-testid="diag-error">
            <AlertTriangle size={18} /> <span>{err}</span>
          </div>
        )}

        {!loading && !err && data && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Total" value={data.total_errors} testid="diag-stat-total" />
              <Stat label="Types uniques" value={data.unique_errors} testid="diag-stat-unique" />
              <Stat label="Utilisateurs touchés" value={data.affected_users} testid="diag-stat-affected" />
              <Stat label="Période" value={`${data.period_days}j`} testid="diag-stat-period" />
            </div>

            {/* AI Summary */}
            <div className="glass rounded-3xl p-6" data-testid="diag-ai-summary-card">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={16} className="text-yellow-400" />
                <h2 className="text-lg font-black">Résumé IA</h2>
              </div>
              {data.total_errors === 0 ? (
                <p className="text-zinc-400">Aucune erreur enregistrée ces {data.period_days} derniers jours. Tout va bien ! 🎉</p>
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-sans leading-relaxed">{data.ai_summary}</pre>
              )}
            </div>

            {/* AI Fix Prompt */}
            {data.ai_fix_prompt && (
              <div className="glass rounded-3xl p-6 border border-pink-500/30" data-testid="diag-fix-prompt-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles size={16} className="text-pink-400" />
                    <h2 className="text-lg font-black">Prompt prêt pour Emergent</h2>
                  </div>
                  <Button onClick={copyPrompt} className="h-9 px-3 bg-pink-500/20 text-pink-200 border-0 rounded-lg" data-testid="diag-copy-prompt-btn">
                    <Copy size={14} className="mr-1.5" /> Copier
                  </Button>
                </div>
                <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-mono leading-relaxed bg-black/30 rounded-xl p-4 max-h-96 overflow-auto">{data.ai_fix_prompt}</pre>
                <p className="text-xs text-zinc-500 mt-2">Copie ce prompt et colle-le dans ton chat Emergent — je le traiterai comme un ticket de bug.</p>
              </div>
            )}

            {/* Top groups */}
            <div className="glass rounded-3xl overflow-hidden" data-testid="diag-groups">
              <div className="p-4 border-b border-white/5 flex items-center gap-2">
                <Bug size={14} className="text-red-400" />
                <h2 className="text-sm font-black uppercase tracking-wider">Top erreurs</h2>
              </div>
              {data.top_groups.length === 0 ? (
                <div className="p-8 text-center text-zinc-500 text-sm">Rien à signaler 👌</div>
              ) : (
                data.top_groups.map((g) => (
                  <div key={g.fingerprint} className="p-4 border-b border-white/5 last:border-0" data-testid={`diag-group-${g.fingerprint}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <Badge className="bg-red-500/15 text-red-300 border-0 font-mono text-xs">{g.count}x</Badge>
                      <span className="text-[10px] text-zinc-500 font-mono">{g.fingerprint}</span>
                    </div>
                    <div className="text-sm font-semibold text-white mb-1 break-words">{g.message}</div>
                    {g.url && <div className="text-xs text-zinc-400 mb-2">↳ {g.url}</div>}
                    {g.stack && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-zinc-500 hover:text-zinc-300">Stack trace</summary>
                        <pre className="mt-1 bg-black/30 rounded-lg p-2 overflow-x-auto text-zinc-400 text-[11px]">{g.stack}</pre>
                      </details>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="glass-light rounded-2xl p-4" data-testid={testid}>
      <div className="text-3xl font-black text-white">{value}</div>
      <div className="text-xs text-zinc-400 mt-1">{label}</div>
    </div>
  );
}
